/** ログ画面の問い合わせ。絞り込みは**そのまま CSV にも渡す**(同じ params を作る)。 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback } from "react";

import type { LogSettings } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useIdpDownload } from "./hooks";

/** 空の絞り込みは送らない(URL を短く保ち、キャッシュの鍵も安定する)。 */
export function logParams(filters: Record<string, string | number | undefined>): URLSearchParams {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === "" || v === null) continue;
    out.set(k, String(v));
  }
  return out;
}

export function useLogStatus(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "logs", "status"],
    queryFn: api.idpLogStatus,
    enabled,
  });
}

/** 手動取込。終わったら一覧と状態を捨てて引き直す。 */
export function useLogPull() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.idpLogPull,
    onSuccess: (out) => {
      const added = out.login.inserted + out.admin.inserted;
      void message.success(added > 0 ? `${added} 件を取り込んだ` : "新しいイベントは無かった");
      void qc.invalidateQueries({ queryKey: ["idp", "logs"] });
    },
    onError: (e) => void message.error(errorText(e)),
  });
}

export function useLogSettings(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "logs", "settings"],
    queryFn: api.idpLogSettings,
    enabled,
  });
}

export function useSaveLogSettings() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<LogSettings>) => api.saveIdpLogSettings(data),
    onSuccess: () => {
      void message.success("ログの設定を保存した");
      void qc.invalidateQueries({ queryKey: ["idp", "logs"] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (e) => void message.error(errorText(e)),
  });
}

export function useLogAccess(params: URLSearchParams) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "logs", "access", params.toString()],
    queryFn: () => api.idpLogAccess(params),
  });
}

export function useLogUserOps(params: URLSearchParams) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "logs", "user-ops", params.toString()],
    queryFn: () => api.idpLogUserOps(params),
  });
}

export function useLogAdminOps(params: URLSearchParams) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "logs", "admin-ops", params.toString()],
    queryFn: () => api.idpLogAdminOps(params),
  });
}

export function useLogSync(params: URLSearchParams) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "logs", "sync", params.toString()],
    queryFn: () => api.idpLogSync(params),
  });
}

export function useLogBulk(params: URLSearchParams) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "logs", "bulk", params.toString()],
    queryFn: () => api.idpLogBulk(params),
  });
}

/** 一括登録 1 回ぶんの行結果(開いた行だけ引く)。 */
export function useLogBulkJob(id: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "logs", "bulk-job", id],
    queryFn: () => api.idpLogBulkJob(id!),
    enabled: id !== null,
  });
}

/** CSV。画面と**同じ絞り込み**で落とす(`limit` / `offset` は渡さない)。 */
export function useLogCsv() {
  const download = useIdpDownload();
  return useCallback(
    (what: string, params: URLSearchParams) => {
      const q = new URLSearchParams(params);
      q.delete("limit");
      q.delete("offset");
      void download(`/api/idp/logs/${what}/export.csv?${q.toString()}`);
    },
    [download],
  );
}
